import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";

const supplierSchema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
  cnpj_cpf: z.string().optional(),
  contato: z.string().optional(),
  telefone: z.string().optional(),
  whatsapp: z.string().optional(),
  email: z.string().email("E-mail inválido").optional().or(z.literal("")),
});

type SupplierFormData = z.infer<typeof supplierSchema>;

interface QuickAddSupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSupplierCreated?: (supplier: any) => void;
}

export function QuickAddSupplierModal({ open, onOpenChange, onSupplierCreated }: QuickAddSupplierModalProps) {
  const queryClient = useQueryClient();
  const { tenantId, loading: tenantLoading } = useTenant();

  const form = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      nome: "",
      cnpj_cpf: "",
      contato: "",
      telefone: "",
      whatsapp: "",
      email: "",
    },
  });

  const createSupplierMutation = useMutation({
    mutationFn: async (data: SupplierFormData) => {
      if (!tenantId) {
        throw new Error("Nenhuma empresa selecionada");
      }

      const { data: supplier, error } = await supabase
        .from("suppliers")
        .insert([{
          nome: data.nome,
          cnpj_cpf: data.cnpj_cpf || null,
          contato: data.contato || null,
          telefone: data.telefone || null,
          whatsapp: data.whatsapp || null,
          email: data.email || null,
          tenant_id: tenantId,
        }])
        .select()
        .single();

      if (error) throw error;
      return supplier;
    },
    onSuccess: (supplier) => {
      toast.success("Fornecedor cadastrado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      if (supplier?.id) {
        void logCrud({ entitySlug: "entity:suppliers", operationSlug: "create", entityId: supplier.id, entityName: supplier.nome, screenSlug: "fornecedores" });
      }
      if (supplier) {
        queryClient.setQueryData(["suppliers"], (current: any) => {
          if (!current) return [supplier];
          const exists = current.some((item: any) => item.id === supplier.id);
          return exists ? current : [supplier, ...current];
        });
        onSupplierCreated?.(supplier);
      }
      onOpenChange(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast.error("Erro ao cadastrar fornecedor: " + error.message);
    },
  });

  const onSubmit = (data: SupplierFormData) => {
    if (!tenantId) {
      toast.error("Selecione uma empresa antes de cadastrar fornecedores.");
      return;
    }
    createSupplierMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="relative">
          <DialogTitle>Cadastro Rápido de Fornecedor</DialogTitle>
          <DialogDescription>
            Preencha as informações do fornecedor. O cadastro será vinculado à empresa atual.
          </DialogDescription>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cnpj_cpf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF/CNPJ</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contato"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pessoa de Contato</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="telefone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whatsapp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>WhatsApp</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createSupplierMutation.isPending || tenantLoading || !tenantId}
              >
                {createSupplierMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Cadastrar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
